import { FC } from 'react'
import styled, { css } from 'styled-components'
import { Text } from './text/textStyles'
import { PrimaryButton, theme } from '@tuhi/components'
import { contentColorStyle } from './buttons/button'
import SecondaryWarningIcon from '@tuhi/components/assets/icons/header/secondaryWarning.svg'
import WarningIcon from '@tuhi/components/assets/icons/header/warning.svg'

const styles = {
    error: css`
        background: ${(p) => p.theme.colors.lightRed};
    `,
    warning: css`
        background: ${(p) => p.theme.colors.lightAmber};
    `,
    information: css`
        background: ${(p) => p.theme.colors.blue.blue400};
        position: relative;

        &::before {
            content: '';
            width: 8px; /* Adjust thickness of the vertical bar */
            height: 100%;
            background: ${(p) =>
                p.theme.colors.blue.blue300}; /* Change to desired blue */
            position: absolute;
            left: 0;
            top: 0;
            border-radius: 4px 0 0 4px; /* Round the edges if needed */
        }
    `,
}

const Container = styled.div<{ $type: AlertType }>`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 16px 24px;
    width: 100%;

    ${(p) => styles[p.$type]}
`

const Content = styled.div<{ $color: string; $iconSize: AlertIconSize }>`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-grow: 1; /* Allows text to take available space */

    ${(p) => contentColorStyle(p.$color)}
`

export type AlertType = 'error' | 'warning' | 'information'
export type AlertIconSize = 'small' | 'medium'

interface Props {
    text: string
    type: AlertType
    iconSize: AlertIconSize
    buttonText?: string
    buttonAction?: () => void
}

export const Alert: FC<Props> = ({
    text,
    type,
    iconSize,
    buttonText,
    buttonAction,
}) => {
    const contentColor = (): string => {
        switch (type) {
            case 'error':
                return theme.colors.primaryRed
            case 'warning':
                return theme.colors.primaryAmber
            case 'information':
                return theme.colors.blue.blue300
        }
    }

    const warningIcon = () => {
        switch (iconSize) {
            case 'small':
                return <SecondaryWarningIcon />
            case 'medium':
                return <WarningIcon />
        }
    }

    return (
        <Container $type={type}>
            <Content $color={contentColor()} $iconSize={iconSize}>
                {warningIcon()}
                <Text size="xSmall" $weight="regular" color="none">
                    {text}
                </Text>
            </Content>

            {buttonAction && buttonText && (
                <PrimaryButton
                    height="small"
                    width="hug"
                    onClick={buttonAction}
                >
                    {buttonText}
                </PrimaryButton>
            )}
        </Container>
    )
}
